import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

const formatTime = (min) => {
  const t = Math.round(Number(min) || 0);
  if (!t) return "—";
  const h = Math.floor(t / 60);
  const m = t % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

export function DevCostModal({ project, onClose, onSaved }) {
  const plates = project.plates || [];
  const saved = project.cost || null;
  // O cálculo salvo só é reaproveitado como ponto de partida se a Plate dele ainda existir.
  const savedPlateExists = !!saved && plates.some((p) => p.id === saved.plate_id);
  const [plateId, setPlateId] = useState(
    (savedPlateExists && saved.plate_id) || plates[0]?.id || ""
  );
  const [quantity, setQuantity] = useState(String(saved?.quantity || 1));
  const [printerId, setPrinterId] = useState(
    savedPlateExists ? saved.printer_id || "" : ""
  );
  const [wastePercent, setWastePercent] = useState(
    String(savedPlateExists && saved.waste_percent != null ? saved.waste_percent : 5)
  );
  const [printers, setPrinters] = useState([]);
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/pricing/context")
      .then(({ data }) => setPrinters(data.printers || []))
      .catch(() => {});
  }, []);

  const plate = plates.find((p) => p.id === plateId);

  const run = async (save) => {
    save ? setSaving(true) : setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${project.id}/cost`, {
        plate_id: plateId || null,
        quantity: Number(quantity || 1),
        printer_id: printerId || null,
        waste_percent: Number(wastePercent || 0),
        extras: [],
        save,
      });
      setCalc(data);
      if (save) {
        toast.success("Cálculo salvo no projeto.");
        onSaved();
        onClose();
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      save ? setSaving(false) : setBusy(false);
    }
  };

  const costs = calc?.result?.costs;

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={saved ? "Refazer cálculo de custo" : "Calcular custo do projeto"}
      description="Sempre calcula com os dados atuais da Plate, dos filamentos e da energia. O cálculo salvo só é substituído quando você confirmar."
      testid="dev-cost-modal"
    >
      <div className="mt-2 flex flex-col gap-4">
        {saved && !savedPlateExists && (
          <p
            className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
            data-testid="dev-cost-stale-warning"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            O cálculo salvo usava a Plate "{saved.plate_name}", que não está mais no projeto.
            Escolha a Plate atual e calcule de novo.
          </p>
        )}
        <Field label="Plate do cálculo">
          <NativeSelect
            value={plateId}
            onChange={(v) => {
              setPlateId(v);
              setCalc(null);
            }}
            options={
              plates.length
                ? plates.map((p) => ({ value: p.id, label: p.name }))
                : [{ value: "", label: "Nenhuma Plate vinculada" }]
            }
            data-testid="dev-cost-plate-select"
          />
        </Field>
        {plate && (
          <p className="text-[11px] text-muted-foreground" data-testid="dev-cost-plate-info">
            Esta Plate produz {plate.output_quantity} un por execução ·{" "}
            {formatTime(plate.print_time_min)} por execução
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Quantidade desejada">
            <Input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setCalc(null);
              }}
              className="font-num"
              data-testid="dev-cost-quantity"
            />
          </Field>
          <Field label="Perda estimada (%)">
            <Input
              type="number"
              min="0"
              step="0.5"
              value={wastePercent}
              onChange={(e) => {
                setWastePercent(e.target.value);
                setCalc(null);
              }}
              className="font-num"
              data-testid="dev-cost-waste"
            />
          </Field>
          <Field label="Impressora">
            <NativeSelect
              value={printerId}
              onChange={(v) => {
                setPrinterId(v);
                setCalc(null);
              }}
              options={[
                { value: "", label: "Padrão" },
                ...printers.map((p) => ({ value: p.id, label: p.name })),
              ]}
              data-testid="dev-cost-printer"
            />
          </Field>
        </div>

        <Button
          onClick={() => run(false)}
          disabled={busy || saving || !plateId}
          variant={calc ? "outline" : "default"}
          data-testid="run-dev-cost-btn"
          className="h-11 rounded-full font-semibold transition-transform active:scale-95"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {calc ? "Calcular de novo" : "Calcular custo"}
        </Button>

        {costs && (
          <div className="space-y-2 rounded-xl border border-white/10 bg-secondary/40 p-4" data-testid="dev-cost-result">
            <div className="grid grid-cols-3 gap-2 border-b border-white/10 pb-2 text-center">
              {[
                ["Execuções", calc.runs],
                ["Peças produzidas", calc.produced],
                ["Tempo total", formatTime(calc.time_min)],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {label}
                  </p>
                  <p className="font-num text-sm" data-testid={`dev-cost-${label}`}>
                    {value ?? "—"}
                  </p>
                </div>
              ))}
            </div>
            {[
              ["Material", costs.material],
              ["Energia", costs.energy],
              ["Máquina / depreciação", costs.machine],
              ["Perdas", costs.waste],
              ["Extras", costs.extras],
            ]
              .filter(([, v]) => v !== undefined)
              .map(([label, value]) => (
                <div key={label} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-num">{brl(value || 0)}</span>
                </div>
              ))}
            <div className="flex items-center justify-between border-t border-white/10 pt-2 text-sm">
              <span>Custo total ({calc.quantity} un)</span>
              <span className="font-num font-medium">{brl(costs.total)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Custo por unidade</span>
              <span className="font-num font-medium text-brand" data-testid="dev-cost-unit">
                {brl(costs.unit)}
              </span>
            </div>
            {calc.produced > calc.quantity && (
              <p className="text-[11px] text-amber-300">
                A Plate produz {calc.output_per_run} un por execução, então{" "}
                {calc.runs} execuções geram {calc.produced} un. O custo por unidade acima considera
                as {calc.quantity} un que você pediu.
              </p>
            )}
            <Button
              onClick={() => run(true)}
              disabled={saving || busy}
              data-testid="save-dev-cost-btn"
              className="mt-2 h-11 w-full gap-2 rounded-full font-semibold"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saved ? "Substituir cálculo salvo" : "Salvar este cálculo"}
            </Button>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
